/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createEffect, createSignal } from 'davaux/client'

export type ColorName =
  | 'blue'
  | 'crimson'
  | 'cyan'
  | 'grape'
  | 'gray'
  | 'green'
  | 'indigo'
  | 'lime'
  | 'orange'
  | 'pink'
  | 'red'
  | 'teal'
  | 'violet'
  | 'yellow'

const SCHEMES: Array<{ name: ColorName; label: string }> = [
  { name: 'gray', label: 'Gray' },
  { name: 'red', label: 'Red' },
  { name: 'crimson', label: 'Crimson' },
  { name: 'pink', label: 'Pink' },
  { name: 'grape', label: 'Grape' },
  { name: 'violet', label: 'Violet' },
  { name: 'indigo', label: 'Indigo' },
  { name: 'blue', label: 'Blue' },
  { name: 'cyan', label: 'Cyan' },
  { name: 'teal', label: 'Teal' },
  { name: 'green', label: 'Green' },
  { name: 'lime', label: 'Lime' },
  { name: 'yellow', label: 'Yellow' },
  { name: 'orange', label: 'Orange' },
]

function injectScheme(name: string) {
  if (typeof document === 'undefined') return
  let el = document.getElementById('dv-scheme-override') as HTMLStyleElement | null
  if (!el) {
    el = document.createElement('style')
    el.id = 'dv-scheme-override'
    document.head.appendChild(el)
  }
  el.textContent = [
    ':root {',
    `  --dv-color-primary-filled: var(--dv-color-${name}-filled);`,
    `  --dv-color-primary-filled-hover: var(--dv-color-${name}-filled-hover);`,
    `  --dv-color-primary-filled-color: var(--dv-color-${name}-filled-color);`,
    `  --dv-color-primary-light: var(--dv-color-${name}-light);`,
    `  --dv-color-primary-light-hover: var(--dv-color-${name}-light-hover);`,
    `  --dv-color-primary-light-color: var(--dv-color-${name}-light-color);`,
    `  --dv-color-primary-outline: var(--dv-color-${name}-outline);`,
    `  --dv-color-primary-outline-hover: var(--dv-color-${name}-outline-hover);`,
    `  --dv-color-primary-outline-color: var(--dv-color-${name}-outline-color);`,
    '}',
  ].join('\n')
}

function generateCSS(name: string): string {
  return [
    ':root {',
    `  --dv-color-primary-filled: var(--dv-color-${name}-filled);`,
    `  --dv-color-primary-filled-hover: var(--dv-color-${name}-filled-hover);`,
    `  --dv-color-primary-filled-color: var(--dv-color-${name}-filled-color);`,
    `  --dv-color-primary-light: var(--dv-color-${name}-light);`,
    `  --dv-color-primary-light-hover: var(--dv-color-${name}-light-hover);`,
    `  --dv-color-primary-light-color: var(--dv-color-${name}-light-color);`,
    `  --dv-color-primary-outline: var(--dv-color-${name}-outline);`,
    `  --dv-color-primary-outline-hover: var(--dv-color-${name}-outline-hover);`,
    `  --dv-color-primary-outline-color: var(--dv-color-${name}-outline-color);`,
    '}',
  ].join('\n')
}

function ColorSchemePicker({ value, compact }: { value?: string; compact?: boolean }) {
  const stored =
    typeof localStorage !== 'undefined' ? (localStorage.getItem('dv-scheme') ?? '') : ''
  const [selected, setSelected] = createSignal(value ?? stored)
  const [open, setOpen] = createSignal(false)

  createEffect(() => {
    const scheme = selected()
    if (!scheme) return
    injectScheme(scheme)
  })

  function handleSelect(name: ColorName) {
    setSelected(name)
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('dv-scheme', name)
    }
  }

  function handleCopy() {
    const scheme = selected()
    if (!scheme || typeof navigator === 'undefined') return
    navigator.clipboard.writeText(generateCSS(scheme)).catch(() => {})
  }

  const swatches = SCHEMES.map(({ name, label }) => (
    <button
      type='button'
      class='dv-scheme-swatch'
      data-active={() => (selected() === name ? '' : undefined)}
      style={`--dv-swatch-color: var(--dv-color-${name}-filled)`}
      title={label}
      aria-label={label}
      aria-pressed={() => selected() === name}
      onClick={() => handleSelect(name)}
    />
  ))

  const copyBtn = (
    <button
      type='button'
      class='dv-button'
      data-variant='default'
      data-size='xs'
      onClick={handleCopy}
      title='Copy primary color CSS to clipboard'
    >
      <span class='dv-button-inner'>
        <span class='dv-button-label'>Copy CSS</span>
      </span>
    </button>
  )

  if (compact) {
    return (
      <div
        class='dv-color-scheme-picker'
        data-compact=''
        data-open={() => (open() ? '' : undefined)}
      >
        <button
          type='button'
          class='dv-scheme-swatch'
          style={() => `--dv-swatch-color: var(--dv-color-${selected() || 'blue'}-filled)`}
          aria-label='Choose color scheme'
          title='Choose color scheme'
          aria-expanded={() => open()}
          aria-haspopup='true'
          onClick={() => setOpen(!open())}
        />
        <button
          type='button'
          class='dv-scheme-overlay'
          aria-label='Close color scheme picker'
          onClick={() => setOpen(false)}
        />
        <div class='dv-scheme-panel'>
          {swatches}
          {copyBtn}
        </div>
      </div>
    )
  }

  return (
    <div class='dv-color-scheme-picker'>
      {swatches}
      {copyBtn}
    </div>
  )
}

export default reactive(ColorSchemePicker)
